import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "leaflet/dist/leaflet.css";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Popup,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import { MultiSelect } from "primereact/multiselect";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { MapHomeControl, MapInfoControl, MapLegendControl } from "../../../common/MapControls/MapControls";
import CommonTable from "../../../common/CommonTable";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const CtrlKeyZoom = () => {
  const [, setIsCtrlPressed] = useState(false);
  const map = useMapEvents({
    keydown(event) {
      if (event.key === "Control") {
        setIsCtrlPressed(true);
        map.scrollWheelZoom.enable();
      }
    },
    keyup(event) {
      if (event.key === "Control") {
        setIsCtrlPressed(false);
        map.scrollWheelZoom.disable();
      }
    },
  });

  useEffect(() => {
    map.scrollWheelZoom.disable();
    return () => {
      map.scrollWheelZoom.enable();
    };
  }, [map]);

  return null;
};

const ReportRoadAccidentData = () => {
  const [selectedYears, setSelectedYears] = useState([]);
  const years = [
    { name: "2025", value: "2025" },
    { name: "2024", value: "2024" },
    { name: "2023", value: "2023" },
    { name: "2022", value: "2022" },
    { name: "2021", value: "2021" },
    { name: "2020", value: "2020" },
  ];

  const [legendVisible, setLegendVisible] = useState(false);
  const [geojsonData, setGeojsonData] = useState(null);
  const legendRef = useRef(null);
  const [markerPositions, setMarkerPositions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterData, setFilterData] = useState([]);

  useEffect(() => {
    fetch("/DATA/OSMbased_adm0_boundary.json")
      .then((response) => response.json())
      .then((data) => setGeojsonData(data))
      .catch((error) =>
        console.error("Error loading boundary data:", error)
      );
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (legendRef.current && !legendRef.current.contains(event.target)) {
        setLegendVisible(false);
      }
    };

    if (legendVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [legendVisible]);

  const handleReportRoadAccidentSearch = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!selectedYears || selectedYears.length === 0) {
      setErrorMessage("Please select at least one year.");
      setMarkerPositions([]);
      setFilterData([]);
      return;
    }

    const yearsInt = selectedYears.map((year) => parseInt(year, 10));

    try {
      const response = await fetch(
        `${backendUrl}roadaccident/filter_reportroadaccident_details/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ year: yearsInt }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(data.error || "Error fetching data");
        setMarkerPositions([]);
        setFilterData([]);
        return;
      }

      if (data.length === 0) {
        setErrorMessage("Data not available");
        setMarkerPositions([]);
        setFilterData([]);
        return;
      }

      const markers = data.map((record) => ({
        lat: parseFloat(record.latitude),
        lng: parseFloat(record.longitude),
        label: record.location || "Road Accident",
      }));
      setMarkerPositions(markers);
      setFilterData(data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setErrorMessage("Error fetching data");
      setMarkerPositions([]);
      setFilterData([]);
    }
  };

  const tableRows = useMemo(
    () =>
      filterData.map((row) => ({
        ...row,
        vehicles:
          [row.motorized_vehicle1, row.motorized_vehicle2, row.motorized_vehicle, row.others]
            .filter(Boolean)
            .join(", ") || "-",
      })),
    [filterData]
  );

  const columns = useMemo(() => [
    { key: "SlNo", label: "S.No" },
    { key: "municipality", label: "Municipality", sortable: true },
    { key: "submunicipality", label: "Sub Municipality", sortable: true },
    { key: "suco", label: "Suco", sortable: true },
    { key: "accident_type", label: "Accident Type", sortable: true },
    { key: "vehicles", label: "Vehicle(s)" },
    { key: "location", label: "Location" },
    { key: "latitude", label: "Latitude", align: "right" },
    { key: "longitude", label: "Longitude", align: "right" },
    {
      key: "datetime",
      label: "Date & Time",
      sortable: true,
      render: (row) => (row.datetime ? new Date(row.datetime).toLocaleString() : "-"),
    },
    { key: "message", label: "Message" },
    { key: "advisory", label: "Advisory", sortable: true },
  ], []);

  const geoJsonStyle = {
    color: "black",
    weight: 1.4,
    fillOpacity: 0,
  };

  const mapCenter = [-9.1747, 125.7275];

  return (
    <>
      <div className="">
        <div className="formcontainer1" style={{ marginBottom: "15px" }}>
          <Form onSubmit={handleReportRoadAccidentSearch}>
            <Row className="align-items-center">
              <Col md={5}>
                <Form.Group className="d-flex align-items-center gap-3">
                  <Form.Label className="mb-0">Year</Form.Label>
                  <MultiSelect
                    value={selectedYears}
                    onChange={(e) => setSelectedYears(e.value)}
                    options={years}
                    optionLabel="name"
                    display="chip"
                    placeholder="Select Year(s)"
                    className="w-full md:w-20rem"
                    style={{ width: "400px", fontSize: "13px", height: "40px" }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>
          {errorMessage && (
            <div style={{ marginTop: "10px", color: "red" }}>
              {errorMessage}
            </div>
          )}
        </div>

        <div className="Blackspotpart2">
          <MapContainer center={mapCenter} zoom={8} className="leaflet-map">
            <MapHomeControl />
            <MapInfoControl text="Reported road accidents. Click a marker to view details." />
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="© OpenStreetMap contributors"
            />
            {geojsonData && <GeoJSON data={geojsonData} style={geoJsonStyle} />}
            {markerPositions.map((marker, index) => (
              <Marker
                key={index}
                position={[marker.lat, marker.lng]}
                icon={L.icon({
                  iconUrl: "/Roadaccidenticons/red.png",
                  iconSize: [20, 20],
                })}
              >
                <Popup>{marker.label}</Popup>
              </Marker>
            ))}
            <CtrlKeyZoom />
            <MapLegendControl
              sections={[{
                items: [
                  { label: "Report Road Accident", icon: <img src="/Roadaccidenticons/red.png" alt="Icon" style={{ width: "18px", height: "18px" }} /> },
                ],
              }]}
            />
          </MapContainer>
        </div>

        {filterData.length > 0 && (
          <div className="formcontainer1" style={{ marginTop: "20px" }}>
            <CommonTable
              tableData={tableRows}
              columns={columns}
              searchableKeys={["municipality", "submunicipality", "suco", "accident_type", "vehicles", "location", "message", "advisory"]}
              tableName="Report_Road_Accident"
              emptyMessage="No road accident reports found."
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ReportRoadAccidentData;
